import { EVERYONE_PRINCIPAL_ID } from '@lucidlink/dto/validation';
import { WorkspaceCreateDto } from '@lucidlink/dto/web-service-api';
import { cryptoKeyToBase64, encryptCryptoKeyWithPublicKey, generateAesGcmKey, generateCryptoPairs, } from '@lucidlink/ts-crypto/crypto';
import { webCrypto } from '@lucidlink/ts-crypto/web-crypto';
import { prepareOwnerInvitation } from '#sdk/common';
import { prepareGuestInvitation } from '#sdk/common/invitation/prepare-guest-invitation';
import { signWorkspaceCryptoTuple } from '#sdk/workspace-client/crypto/crypto-utils';
import { prepareWorkspaceCrypto } from '#sdk/workspace-client/prepare-ensure-sso-crypto';
export const generateWorkspaceCreateKeys = async () => {
    const adminKeysPromise = generateCryptoPairs();
    const everyoneKeysPromise = generateCryptoPairs();
    const workspaceCommonKeyPromise = generateAesGcmKey();
    return {
        adminKeys: await adminKeysPromise,
        everyoneKeys: await everyoneKeysPromise,
        workspaceCommonKey: await workspaceCommonKeyPromise,
    };
};
export const prepareWorkspace = async ({ name, awsMarketplaceContractId, isNonZeroKnowledge, partnerOrganizationId, }, userKey, workspaceCreateKeys) => {
    const workspaceId = webCrypto.randomUUID();
    const { adminKeys, everyoneKeys, workspaceCommonKey } = workspaceCreateKeys ?? (await generateWorkspaceCreateKeys());
    const workspaceCrypto = await prepareWorkspaceCrypto({
        workspaceId,
        adminCrypto: {
            verifyAndGetAdminKeys: () => Promise.resolve(adminKeys),
            verifyAndGetEveryoneKeys: () => Promise.resolve(everyoneKeys.encryptPair),
        },
        includeWorkspaceKeyInDto: isNonZeroKnowledge,
    });
    const invitationPromise = prepareOwnerInvitation({
        userKey,
        adminKeys,
        workspaceCommonKey,
        everyonePrivateKey: everyoneKeys.encryptPair.privateKey,
        workspaceId,
        workspaceSecretKey: workspaceCrypto.keys.workspaceSecretKey,
    });
    const adminPublicKeyPromise = cryptoKeyToBase64(adminKeys.encryptPair.publicKey);
    const everyonePublicKeyPromise = cryptoKeyToBase64(everyoneKeys.encryptPair.publicKey);
    const eEveryonePrivateKeyByAdminKeyPromise = encryptCryptoKeyWithPublicKey(everyoneKeys.encryptPair.privateKey, adminKeys.encryptPair.publicKey);
    const sEveryoneByAdminKeyPromise = signWorkspaceCryptoTuple({
        id: EVERYONE_PRINCIPAL_ID,
        workspaceId,
        publicKey: everyoneKeys.encryptPair.publicKey,
    }, adminKeys.signPair.privateKey);
    const eWorkspaceCommonKeyByAdminKeyPromise = encryptCryptoKeyWithPublicKey(workspaceCommonKey, adminKeys.encryptPair.publicKey);
    const guestInvitationPromise = prepareGuestInvitation({
        adminKeys,
        workspaceCommonKey,
        everyonePrivateKey: everyoneKeys.encryptPair.privateKey,
        workspaceId,
        workspaceSecretKey: workspaceCrypto.keys.workspaceSecretKey,
    });
    const [adminPublicKey, everyonePublicKey, eEveryonePrivateKeyByAdminKey, sEveryoneByAdminKey, eWorkspaceCommonKeyByAdminKey, invitation, guestInvitation,] = await Promise.all([
        adminPublicKeyPromise,
        everyonePublicKeyPromise,
        eEveryonePrivateKeyByAdminKeyPromise,
        sEveryoneByAdminKeyPromise,
        eWorkspaceCommonKeyByAdminKeyPromise,
        invitationPromise,
        guestInvitationPromise,
    ]);
    const workspaceRequest = {
        id: workspaceId,
        name,
        adminPublicKey,
        everyonePublicKey,
        eEveryonePrivateKeyByAdminKey,
        sEveryoneByAdminKey,
        eWorkspaceCommonKeyByAdminKey,
        invitation,
        awsMarketplaceContractId,
        workspaceCrypto: workspaceCrypto.workspaceCryptoDto,
        guestInvitation,
        partnerOrganizationId,
    };
    return WorkspaceCreateDto.create(workspaceRequest);
};
